/**
 * UpdatePosit.tsx - UpdatePosit Page
 * 
 * Page component for updating posits with AI assistance.
 * Uses V1 implementation directly without version UI.
 */

import { useNavigate, useLocation, useParams, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import UpdatePositChatBotV1 from "@/features/update-posit/v1/UpdatePositChatBotV1";
// TODO: Import V3 component when available
import { Loader2 } from "lucide-react";
import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";
import { FileAttachment } from "@/types/posit";
import { Helmet } from "react-helmet-async";
import { fetchPositAttachments } from "@/utils/fileUpload";
import { useToast } from "@/hooks/use-toast";
import ChatLayout from "@/components/chat/layout/ChatLayout";
import { ChatInterfaceProvider } from "@/contexts/ChatInterfaceContext";
import PositDeleted from "@/components/posit/PositDeleted";
import { useQuery } from "@tanstack/react-query";

const UpdatePosit = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { positId } = useParams<{ positId: string }>();
  const [searchParams] = useSearchParams();
  const conversationId = searchParams.get('conversation');
  const { user } = useAuth();
  const { toast } = useToast();
  const isNewPosit = !positId;
  const [isCheckingPosit, setIsCheckingPosit] = useState(!isNewPosit);
  const [positExists, setPositExists] = useState<boolean | null>(null);
  const [positTitle, setPositTitle] = useState<string | null>(null);
  const hasNavigatedRef = useRef(false);
  const [isLoadingFiles, setIsLoadingFiles] = useState(false);
  const [initialFiles, setInitialFiles] = useState<FileAttachment[]>([]);
  const [isInitialLoading, setIsInitialLoading] = useState(true);

  // Query to check if posit exists (for existing posits only)
  const { data: positData, isLoading: isPositLoading } = useQuery({
    queryKey: ["posit-exists", positId],
    queryFn: async () => {
      if (!positId || !user?.id) return null;

      const { data, error } = await supabase
        .from("posits")
        .select("id, title, user_id")
        .eq("id", positId)
        .eq("user_id", user.id)
        .maybeSingle();

      if (error) throw error;
      return data;
    },
    enabled: !!positId && !!user?.id,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Update posit existence state based on query
  useEffect(() => {
    if (!isNewPosit) {
      if (!isPositLoading) {
        setPositExists(!!positData);
        setPositTitle(positData?.title || null);
        setIsCheckingPosit(false);
      }
    } else {
      setPositExists(true);
      setIsCheckingPosit(false);
    }
  }, [isNewPosit, isPositLoading, positData]);

  // Load initial files for existing posits
  const loadInitialFiles = useCallback(async () => {
    if (!positId || !user?.id || isNewPosit) {
      setIsInitialLoading(false);
      return;
    }

    try {
      setIsLoadingFiles(true);
      const files = await fetchPositAttachments(positId);
      setInitialFiles(files);
    } catch (error) {
      toast({
        variant: "destructive", 
        title: "Error",
        description: "Failed to load existing files"
      });
    } finally {
      setIsLoadingFiles(false);
      setIsInitialLoading(false);
    }
  }, [positId, user?.id, isNewPosit, toast]);

  useEffect(() => {
    loadInitialFiles();
  }, [loadInitialFiles]);

  // Dynamic page title and meta description
  const pageTitle = useMemo(() => {
    if (isNewPosit) return "Create New Posit";
    return positTitle ? `Update ${positTitle}` : "Update Posit";
  }, [isNewPosit, positTitle]);

  // Handle authentication redirect
  useEffect(() => {
    if (!user && !hasNavigatedRef.current) {
      hasNavigatedRef.current = true;
      navigate("/login", { 
        state: { from: location.pathname },
        replace: true 
      });
    }
  }, [user, navigate, location.pathname]);

  if (isInitialLoading) {
    return (
      <div className="flex flex-col h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary mb-4" />
        <p className="text-muted-foreground">Initializing...</p>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  // Check if posit exists and user has access (for existing posits)
  if (!isNewPosit && !isCheckingPosit && positExists === false) {
    return (
      <PositDeleted
        context="edit"
        isAuthenticated={!!user}
        positId={positId}
      />
    );
  }

  return (
    <div className="h-screen w-screen bg-background relative overflow-hidden">
      <Helmet>
        <title>{pageTitle} | Posit</title>
        <meta name="description" content={`${isNewPosit ? 'Create a new' : 'Update your'} posit with AI-assisted chat`} />
      </Helmet>
      
      <ChatInterfaceProvider>
        <ChatLayout 
          mobileTitle={isNewPosit ? "Create Posit" : "Update Posit"}
          showMobileHeader={true}
        >
          <div className="flex-1 overflow-hidden relative">
            {user ? (
              <UpdatePositChatBotV1 
                isNewPosit={isNewPosit} 
                positId={positId}
                conversationId={conversationId}
                lazyLoadMessages={false}
                positTitle={positTitle}
              />
            ) : (
              <div className="h-full flex items-center justify-center">
                <p className="text-muted-foreground">
                  Please log in to update your Posit.
                </p>
              </div>
            )}
          </div>
        </ChatLayout>
      </ChatInterfaceProvider>
    </div>
  );
};

export default UpdatePosit;
